import { downloadFromLink } from "client/utils/downloadFromLink";
import { getImageUrl } from "client/utils/getImageUrl";
import { loadImage } from "client/utils/imageEdit/loadImage";
import { convertToPixels } from "client/utils/pixels/convertToPixels";
import { useContext, useRef, useState } from "react";
import { validateResizeDimension } from "client/utils/validateResizeDimension";
import { enlargeSelector } from "store/features/enlarge/selectors/enlargeSelector";
import { store, useAppDispatch, useAppSelector } from "store/store";
import { MAX_RESIZE_SIZE_IN_PX } from "../Preview/Preview";
import { ga } from "client/utils/adsense/ga";
import { gtag } from "client/utils/adsense/gtag";
import { CanvasContext } from "client/contexts/ResizeEditor/Canvas";
import { useProcessingInFC } from "../Preview/useProcessingInFC";
import { getFileData } from "client/utils/getFileData";
import { createResizeJob, ResizeJobPayload } from "store/features/resizeEditor/extraReducers/createResizeJob";
import { getImageExtensionFromTitle } from "client/utils/getImageExtension";
import { Job, Task } from "@freeconvert/freeconvert-node/dist/types";
import { PayloadAction } from "@reduxjs/toolkit";
import { useToast } from "@/components/common/Toast/useToast";
import { translateFunc } from "@/utils/translateFunc";
import { useDailyQuota } from "@/modules/DailyQuota/hooks/useDailyQuota";

export type ProcessingState =
    | {
          state: "inactive";
      }
    | {
          state: "loading_image";
      }
    | {
          state: "image_loaded";
      }
    | {
          state: "creating_import_task";
          failed?: boolean;
      }
    | {
          state: "uploading";
          progress: number;
          failed?: boolean;
      }
    | {
          state: "creating_job";
          failed?: boolean;
      }
    | {
          state: "processing_job";
          failed?: boolean;
      }
    | {
          state: "job_succeeded";
      };

export const useAction = () => {
    const { initialImageRef } = useContext(CanvasContext);
    const { resizeSettings, image } = useAppSelector(enlargeSelector);
    const [processingState, setProcessingState] = useState<ProcessingState>({ state: "inactive" });
    const { getImportTask, uploadFile, processJob } = useProcessingInFC();
    const dispatch = useAppDispatch();
    const { toast } = useToast();
    const { consumeQuota, consumeQuotaByFrontendTool } = useDailyQuota();
    const isQuotaConsumedRef = useRef<boolean>(false);

    const isProcessing =
        processingState.state !== "inactive" &&
        processingState.state !== "job_succeeded" &&
        !(processingState as any).failed;

    const dimensionsValid = validateResizeDimension(
        image,
        resizeSettings.dimensionType,
        resizeSettings.unit,
        resizeSettings.byPercentage.percentage,
        MAX_RESIZE_SIZE_IN_PX,
    );

    const handleDownloadAnalytics = () => {
        ga("send", {
            hitType: "pageview",
            page: "/vp/image_enlarger/export_started/",
            title: "Image Enlarger Export Started",
        });

        ///event
        gtag("event", "Export Started", {
            event_category: "Image Enlarger",
            event_label: "Editor Page",
        });
    };

    const handleStartAction = async () => {
        if (isProcessing || !dimensionsValid) return;

        handleDownloadAnalytics();
        setProcessingState({ state: "loading_image" });

        const { image: _img } = await loadImage(
            initialImageRef?.current?.getAttribute("src") || getImageUrl(image.src),
        );
        const size = await getImageSizeInKB(_img);

        setProcessingState({ state: "image_loaded" });

        if (size >= 20000) {
            try {
                await handleOverSizeImage(_img.src);
            } catch (e) {
                setProcessingState({ state: "inactive" });
            }
        } else {
            await handleReqularSizeImage(_img);
        }
    };

    const handleReqularSizeImage = async (imgElement: HTMLImageElement) => {
        setProcessingState({ state: "processing_job" });
        const { width: canvasWidth, height: canvasHeight } = getNewSizeInPixels();
        let imageWidth = getImageWidth(canvasWidth, canvasHeight);
        let imageHeight = canvasHeight;

        // adjusting the height and width if width overflows
        if (imageWidth > canvasWidth) {
            imageWidth = canvasWidth;
            imageHeight = (image.original.height / image.original.width) * imageWidth;
        }

        const canvas = document.createElement("canvas");
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        const context = canvas.getContext("2d");
        if (!context) return;

        if (!resizeSettings.isLocked && resizeSettings.fillColor.shouldFillColor) {
            if (resizeSettings.fillColor.type === "color") {
                context.fillStyle = resizeSettings.fillColor.color;
            } else {
                context.fillStyle = `rgba(0,0,0,0)`;
            }
            context.fillRect(0, 0, canvasWidth, canvasHeight);
        }

        context.drawImage(
            imgElement,
            (canvasWidth - imageWidth) / 2,
            (canvasHeight - imageHeight) / 2,
            imageWidth,
            imageHeight,
        );
        if (!isQuotaConsumedRef.current) {
            consumeQuotaByFrontendTool();
            isQuotaConsumedRef.current = true;
        }
        downloadFromLink(canvas.toDataURL("image/png"), image.title);
        setProcessingState({ state: "job_succeeded" });
    };

    const handleOverSizeImage = async (src: string) => {
        const mimeType = `image\\${image.format}`;
        const file = await fetch(src)
            .then((res) => res.blob())
            .then((blob) => {
                return new File([blob], image.title, { type: mimeType });
            })
            .catch(() => null);
        if (!file) {
            setProcessingState({ state: "processing_job", failed: true });
            showResizeErrorToast();
            return;
        }
        setProcessingState({ state: "creating_import_task" });
        const importTask = await getImportTask();
        if (!importTask) {
            setProcessingState({ state: "creating_import_task", failed: true });
            showResizeErrorToast();
            return;
        }
        setProcessingState({ state: "uploading", progress: 0 });
        try {
            await uploadFile(getFileData(file), importTask, (progress) => {
                setProcessingState({ state: "uploading", progress, failed: false });
            });
        } catch (e) {
            setProcessingState({ state: "uploading", progress: 0, failed: true });
            showResizeErrorToast();
            return;
        }
        setProcessingState({ state: "creating_job" });
        const job = await createJob(importTask);
        if (!job) {
            setProcessingState({ state: "creating_job", failed: true });
            showResizeErrorToast();
            return;
        }
        setProcessingState({ state: "processing_job" });

        try {
            const resultJob = await processJob(job);
            const exportTask = resultJob.tasks?.find((task) => task.name === "exportTask");
            const src = exportTask?.result?.url?.toString();
            if (!src) {
                setProcessingState({ state: "processing_job", failed: true });
                showResizeErrorToast();
                return;
            }
            setProcessingState({ state: "job_succeeded" });
            if (!isQuotaConsumedRef.current) {
                consumeQuota(1);
                isQuotaConsumedRef.current = true;
            }
            downloadFromLink(src, image.title);
        } catch (e) {
            setProcessingState({ state: "processing_job", failed: true });
        }
    };

    const errorMessage = translateFunc("processing_error_message");
    const showResizeErrorToast = () => {
        toast({
            position: "top-right",
            status: "error",
            text: errorMessage,
            closeable: true,
            time: 7000,
        });
    };

    const createJob = async (importTask: Task): Promise<Job | null> => {
        const jobPayload = getResizeJobPayload(importTask);
        const result = await dispatch(createResizeJob(jobPayload));
        if (result.meta.requestStatus === "rejected") return null;
        const payload = result.payload as PayloadAction<{ result: Job }>["payload"];
        return payload.result;
    };

    const getResizeJobPayload = (importTask: Task): ResizeJobPayload => {
        const resize = store.getState().enlarge.resizeSettings;
        const image = store.getState().enlarge.image;
        const format = getImageExtensionFromTitle(image.title);
        const resizeData: ResizeJobPayload["resize"] = {
            dimensions: getNewSizeInPixels(),
            inputFormat: format,
            options: {
                dpi: resize.dimensionType === "pixels" && resize.unit !== "pixels" ? parseInt(resize.dpi) : undefined,
                background: resize.fillColor.shouldFillColor
                    ? resize.fillColor.type === "color"
                        ? resize.fillColor.color
                        : "none"
                    : undefined,
                fillBackground:
                    resize.dimensionType === "percentage"
                        ? false
                        : resize.isLocked
                          ? false
                          : resize.fillColor.shouldFillColor,
                rotation: 0,
                horizontalFlip: false,
                verticalFlip: false,
            },
            outputFormat: format,
            percentage: null,
            resize_as: "dimensions",
        };

        return {
            crop: null,
            resize: resizeData,
            importTask,
        };
    };

    const shouldRetainIntentedSize =
        resizeSettings.dimensionType === "percentage" ||
        resizeSettings.isLocked ||
        !resizeSettings.fillColor.shouldFillColor;

    const getImageWidth = (canvasWidth: number, canvasHeight: number): number => {
        if (shouldRetainIntentedSize) {
            return canvasWidth;
        }

        const ratio = image.original.width / image.original.height;
        return canvasHeight * ratio;
    };

    const getNewSizeInPixels = (): { width: number; height: number } => {
        if (resizeSettings.dimensionType === "percentage") {
            return {
                width: (image.original.width * resizeSettings.byPercentage.percentage) / 100,
                height: (image.original.height * resizeSettings.byPercentage.percentage) / 100,
            };
        }

        return {
            width: convertToPixels(resizeSettings.byDimension.width, resizeSettings.unit),
            height: convertToPixels(resizeSettings.byDimension.height, resizeSettings.unit),
        };
    };

    function getImageSizeInKB(imageElement: HTMLImageElement): Promise<number> {
        return new Promise((resolve, reject) => {
            if (!imageElement || !imageElement.src) {
                reject(new Error("Invalid image element or source"));
                return;
            }

            fetch(imageElement.src)
                .then((response) => {
                    if (!response.ok) {
                        reject(new Error("Failed to fetch the image"));
                        return;
                    }
                    return response.blob();
                })
                .then((blob) => {
                    const sizeInBytes = blob!.size;
                    const sizeInKB = sizeInBytes / 1024;
                    resolve(sizeInKB);
                })
                .catch((error) => {
                    reject(new Error("Error fetching image: " + error.message));
                });
        });
    }

    return {
        handleStartAction,
        processingState,
        dimensionsValid,
        isProcessing,
    };
};
